import { isValidDocument, normalizeDocument } from '@aeris/party'

/**
 * Buscar os dados de uma empresa pelo CNPJ — F130.
 *
 * O pedido do dono, olhando o "Novo cadastro": *"quando colocar o CNPJ tem que
 * pegar os dados da Receita e já preencher tudo, e o usuário editar"*.
 *
 * ## O mesmo papel da busca de CEP (F089)
 *
 * Um assistente, não uma etapa. Não valida nada, não trava campo nenhum e não
 * é obrigatória: preenche o que está vazio e sai do caminho. Sem rede, com o
 * serviço fora do ar ou com um CNPJ recém-aberto que ainda não chegou à base
 * pública, o formulário continua exatamente como era — tudo digitável.
 *
 * ## De onde vem, e por que direto do navegador
 *
 * A Receita não publica uma API de consulta gratuita; o que existe são serviços
 * que republicam os **dados abertos do CNPJ**. Dois foram conferidos ao vivo em
 * 13/09/2026, os dois com `Access-Control-Allow-Origin: *` e os dois aceitando
 * o CNPJ alfanumérico (`00000000E08G12`, o primeiro emitido):
 *
 * - **BrasilAPI** (`brasilapi.com.br/api/cnpj/v1/{cnpj}`) — primeiro;
 * - **CNPJá aberta** (`open.cnpja.com/office/{cnpj}`) — se o primeiro falhar.
 *
 * Direto do navegador pela mesma razão do CEP: a API da loja não precisa de
 * saída para a internet por uma consulta a um dado público de empresa, que não
 * envolve nada do tenant. O domínio não conhece nenhum dos dois — só esta
 * tela, e trocar de serviço é trocar este arquivo.
 *
 * **Inscrição estadual não vem**: nenhum dos dois a publica de graça (é o
 * SINTEGRA de cada estado). Continua digitada.
 *
 * **Nada disto entra em importação em massa**: os dois serviços limitam por
 * minuto, e a busca só dispara com um CNPJ válido completo, uma vez por sessão.
 */

export type CnpjAddress = {
  cep: string
  street: string
  number: string
  complement: string
  district: string
  city: string
  uf: string
  ibgeCode: string
}

export type CnpjCompany = {
  /** O CNPJ normalizado, 14 posições. */
  document: string
  legalName: string
  tradeName: string
  /** A situação cadastral como a Receita escreve: "Ativa", "Baixada"… */
  status: string
  active: boolean
  address: CnpjAddress | null
  /** Telefones com DDD, só dígitos. */
  phones: string[]
  emails: string[]
}

export const BRASILAPI_ENDPOINT = 'https://brasilapi.com.br/api/cnpj/v1'
export const CNPJA_ENDPOINT = 'https://open.cnpja.com/office'

const textOf = (value: unknown): string =>
  typeof value === 'string'
    ? value.trim()
    : typeof value === 'number'
      ? String(value)
      : ''

const digitsOf = (value: unknown): string => textOf(value).replace(/\D/g, '')

/** Preposições que ficam em minúscula no meio de um nome. */
const LOWER_WORDS = new Set(['de', 'da', 'do', 'das', 'dos', 'e'])

/**
 * `SAUN QUADRA 5` → `Saun Quadra 5`.
 *
 * A BrasilAPI escreve endereço em caixa alta, e um endereço gritado na ficha
 * e na nota é mais difícil de conferir. A razão social NÃO passa por aqui: é o
 * nome registrado, e quem emite nota o copia como a Receita escreveu.
 */
export const titleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(/(\s+)/)
    .map((word, index) =>
      index > 0 && LOWER_WORDS.has(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join('')

const addressOrNull = (address: CnpjAddress): CnpjAddress | null =>
  address.city === '' || address.uf === '' ? null : address

const uniqueNonEmpty = (values: readonly string[]): string[] => [
  ...new Set(values.filter((value) => value !== '')),
]

/** A resposta da BrasilAPI, do jeito que ela devolve (conferida em 13/09/2026). */
export const parseBrasilApi = (
  document: string,
  body: Record<string, unknown>
): CnpjCompany | null => {
  const legalName = textOf(body.razao_social)
  if (legalName === '') return null

  const kind = textOf(body.descricao_tipo_de_logradouro)
  const street = textOf(body.logradouro)
  // O tipo vem separado ("AVENIDA" + "PAULISTA"), e às vezes já dentro do
  // logradouro — não repetir.
  const fullStreet =
    kind !== '' && !street.toUpperCase().startsWith(kind.toUpperCase())
      ? `${kind} ${street}`
      : street

  const status = textOf(body.descricao_situacao_cadastral)

  return {
    document,
    legalName,
    tradeName: titleCase(textOf(body.nome_fantasia)),
    status: titleCase(status),
    active: status.toUpperCase() === 'ATIVA',
    address: addressOrNull({
      cep: digitsOf(body.cep),
      street: titleCase(fullStreet),
      number: textOf(body.numero),
      complement: titleCase(textOf(body.complemento)),
      district: titleCase(textOf(body.bairro)),
      city: titleCase(textOf(body.municipio)),
      uf: textOf(body.uf).toUpperCase(),
      ibgeCode: digitsOf(body.codigo_municipio_ibge),
    }),
    phones: uniqueNonEmpty([
      digitsOf(body.ddd_telefone_1),
      digitsOf(body.ddd_telefone_2),
    ]),
    emails: uniqueNonEmpty([textOf(body.email).toLowerCase()]),
  }
}

type CnpjaBody = {
  alias?: unknown
  company?: { name?: unknown }
  status?: { text?: unknown }
  address?: {
    zip?: unknown
    street?: unknown
    number?: unknown
    details?: unknown
    district?: unknown
    city?: unknown
    state?: unknown
    municipality?: unknown
  }
  phones?: unknown
  emails?: unknown
}

/** A resposta da CNPJá aberta (conferida em 13/09/2026). */
export const parseCnpja = (
  document: string,
  body: CnpjaBody
): CnpjCompany | null => {
  const legalName = textOf(body.company?.name)
  if (legalName === '') return null

  const status = textOf(body.status?.text)
  const address = body.address ?? {}
  const phones = Array.isArray(body.phones) ? body.phones : []
  const emails = Array.isArray(body.emails) ? body.emails : []

  return {
    document,
    legalName,
    tradeName: textOf(body.alias),
    status,
    active: status.toLowerCase() === 'ativa',
    address: addressOrNull({
      cep: digitsOf(address.zip),
      street: textOf(address.street),
      number: textOf(address.number),
      complement: textOf(address.details),
      district: textOf(address.district),
      city: textOf(address.city),
      uf: textOf(address.state).toUpperCase(),
      ibgeCode: digitsOf(address.municipality),
    }),
    phones: uniqueNonEmpty(
      phones.map((phone: unknown) => {
        const entry = (phone ?? {}) as { area?: unknown; number?: unknown }
        return `${digitsOf(entry.area)}${digitsOf(entry.number)}`
      })
    ),
    emails: uniqueNonEmpty(
      emails.map((email: unknown) =>
        textOf((email as { address?: unknown } | null)?.address).toLowerCase()
      )
    ),
  }
}

const CACHE = new Map<string, CnpjCompany | null>()

/** Só para os testes: cada caso começa sem o que o anterior guardou. */
export const clearCnpjCache = (): void => {
  CACHE.clear()
}

const fetchJson = async (
  url: string,
  signal: AbortSignal | undefined
): Promise<Record<string, unknown> | null> => {
  try {
    const response = await fetch(url, signal === undefined ? {} : { signal })
    if (!response.ok) return null
    const body: unknown = await response.json()
    return typeof body === 'object' && body !== null
      ? (body as Record<string, unknown>)
      : null
  } catch {
    // Rede fora, CORS, `AbortError`. Quem chama tenta o próximo, ou segue sem.
    return null
  }
}

/**
 * `null` para tudo que não seja uma empresa utilizável — CNPJ inválido, não
 * encontrado, os dois serviços fora. Nunca lança: quem chama está no meio de um
 * cadastro com o cliente na frente.
 */
export const lookupCnpj = async (
  raw: string,
  signal?: AbortSignal
): Promise<CnpjCompany | null> => {
  const document = normalizeDocument('company', raw)
  // Só com os dígitos verificadores certos: um CNPJ digitado pela metade não
  // vira uma consulta, e um errado não vira duas.
  if (!isValidDocument('company', document)) return null

  const cached = CACHE.get(document)
  if (cached !== undefined) return cached

  const fromBrasilApi = await fetchJson(
    `${BRASILAPI_ENDPOINT}/${document}`,
    signal
  )
  const first =
    fromBrasilApi === null ? null : parseBrasilApi(document, fromBrasilApi)
  if (first !== null) {
    CACHE.set(document, first)
    return first
  }

  const fromCnpja = await fetchJson(`${CNPJA_ENDPOINT}/${document}`, signal)
  const second = fromCnpja === null ? null : parseCnpja(document, fromCnpja)
  // Só o que foi ACHADO fica guardado: uma falha de rede tem de poder ser
  // tentada de novo daqui a um minuto.
  if (second !== null) CACHE.set(document, second)
  return second
}

/**
 * O que a busca pode escrever num campo de texto — a regra do CEP (F089).
 *
 * Só quando o campo está vazio, ou quando ainda tem exatamente o que a busca
 * ANTERIOR pôs nele. É o que permite corrigir um CNPJ digitado errado — a
 * correção troca a razão social inteira — sem apagar o que a pessoa escreveu
 * por cima.
 */
export const fillableWith = (
  current: string,
  found: string,
  previous: string | null
): string => {
  const typed = current.trim()
  if (found === '') return current
  if (typed === '' || (previous !== null && typed === previous)) return found
  return current
}

/**
 * O endereço da Receita sobre o que já está no formulário, campo a campo, pela
 * mesma regra de `fillableWith`. A UF só é trocada junto com o município — uma
 * UF sozinha, sobre uma cidade digitada, montaria um endereço que não existe.
 */
export const mergeCnpjAddress = (
  current: CnpjAddress,
  found: CnpjAddress,
  previous: CnpjAddress | null
): CnpjAddress => {
  const city = fillableWith(current.city, found.city, previous?.city ?? null)
  return {
    cep: fillableWith(current.cep, found.cep, previous?.cep ?? null),
    street: fillableWith(
      current.street,
      found.street,
      previous?.street ?? null
    ),
    number: fillableWith(
      current.number,
      found.number,
      previous?.number ?? null
    ),
    complement: fillableWith(
      current.complement,
      found.complement,
      previous?.complement ?? null
    ),
    district: fillableWith(
      current.district,
      found.district,
      previous?.district ?? null
    ),
    city,
    ibgeCode: fillableWith(
      current.ibgeCode,
      found.ibgeCode,
      previous?.ibgeCode ?? null
    ),
    uf: city === found.city ? found.uf : current.uf,
  }
}

/** Celular tem nove dígitos depois do DDD, começando por 9. */
export const phoneKindOf = (digits: string): 'mobile' | 'phone' =>
  digits.length === 11 && digits.charAt(2) === '9' ? 'mobile' : 'phone'
